/**
 * Leagă un model încărcat de simulare, după numele din convenție: uși, capotă, portbagaj, roți
 * (rotire și virare), volan cu raportul real al direcției, pedale, ace de bord, lumini, interior.
 */
import { Group, Material, Mesh, Object3D, Quaternion, Vector3, type Texture } from 'three';
import type { VehicleDefinition } from '../data/types';
import { blenderAxis, PARTS } from './convention';

const OPENABLE = ['door_FL', 'door_FR', 'door_RL', 'door_RR', 'hood', 'trunk', 'tailgate'] as const;
export type Openable = (typeof OPENABLE)[number];
const WHEELS = ['FL', 'FR', 'RL', 'RR'] as const;

interface Hinge {
  node: Object3D;
  base: Quaternion;
  axis: Vector3;
  maxRad: number;
  value: number;
}

export interface LightState {
  low?: boolean;
  high?: boolean;
  drl?: boolean;
  tail?: boolean;
  brake?: boolean;
  reverse?: boolean;
  indicatorL?: boolean;
  indicatorR?: boolean;
}

export interface CarRig {
  root: Object3D;
  found: string[];
  missing: string[];
  setOpen(part: Openable, fraction: number): void;
  isOpenable(part: Openable): boolean;
  /** Unghiul roților față, radiani (pozitiv = virare la stânga). Volanul se rotește de rackRatio ori mai mult. */
  setSteering(roadWheelRad: number): void;
  steeringWheelDeg(): number;
  /** Unghiul de rotație al fiecărei roți, radiani (FL, FR, RL, RR). */
  setWheelSpin(angles: [number, number, number, number]): void;
  setPedals(p: { throttle?: number; brake?: number; clutch?: number }): void;
  setGauges(g: { rpm?: number; speedKmh?: number; tempC?: number; fuel?: number }): void;
  setLights(l: LightState): void;
  setInteriorDetail(on: boolean): void;
  setPaint(hex: string): void;
  dispose(): number;
}

function hingeFor(node: Object3D, axisName: string, maxDeg: number): Hinge {
  return { node, base: node.quaternion.clone(), axis: new Vector3(...blenderAxis(axisName)), maxRad: (maxDeg * Math.PI) / 180, value: 0 };
}

function apply(h: Hinge, rad: number): void {
  h.node.quaternion.copy(h.base).multiply(new Quaternion().setFromAxisAngle(h.axis, rad));
}

function extras(o: Object3D): Record<string, unknown> {
  return (o.userData ?? {}) as Record<string, unknown>;
}

export function buildRig(root: Object3D, v: VehicleDefinition): CarRig {
  const byName = new Map<string, Object3D>();
  root.traverse((o) => { if (o.name && !byName.has(o.name)) byName.set(o.name, o); });
  const found = [...PARTS.keys()].filter((n) => byName.has(n));
  const missing = [...PARTS.keys()].filter((n) => !byName.has(n));

  const hinges = new Map<string, Hinge>();
  for (const name of OPENABLE) {
    const o = byName.get(name);
    if (!o) continue;
    const rule = PARTS.get(name)!;
    const e = extras(o);
    hinges.set(name, hingeFor(o, String(e.axis ?? rule.anim?.axis ?? 'Z'), Number(e.open_deg ?? rule.anim?.openDeg ?? 60)));
  }
  const knuckles = WHEELS.map((k) => byName.get(`knuckle_${k}`));
  const knuckleBase = knuckles.map((k) => k?.quaternion.clone());
  const wheels = WHEELS.map((k) => byName.get(`wheel_${k}`));
  const wheelHinges = wheels.map((w) => (w ? hingeFor(w, String(extras(w).axis ?? 'X'), 0) : null));
  const sw = byName.get('steering_wheel');
  const swHinge = sw ? hingeFor(sw, String(extras(sw).axis ?? 'Y'), 0) : null;
  const pedals = (['throttle', 'brake', 'clutch'] as const).map((p) => {
    const o = byName.get(`pedal_${p}`);
    const rule = PARTS.get(`pedal_${p}`);
    return o ? { key: p, h: hingeFor(o, String(extras(o).axis ?? 'X'), Number(extras(o).travel_deg ?? rule?.anim?.travelDeg ?? -15)) } : null;
  });
  const needles = (['rpm', 'speed', 'temp', 'fuel'] as const).map((g) => {
    const o = byName.get(`gauge_needle_${g}`);
    if (!o) return null;
    const e = extras(o);
    return { key: g, h: hingeFor(o, String(e.axis ?? 'Y'), 0), min: Number(e.min_value ?? 0), max: Number(e.max_value ?? 1), a0: Number(e.min_deg ?? 120), a1: Number(e.max_deg ?? -120) };
  });

  // materialele: o copie pe instanță, ca două mașini să nu-și împartă luminile sau vopseaua
  const mats = new Map<string, Material & { emissiveIntensity?: number; color?: { set(c: string): void } }>();
  root.traverse((o) => {
    const m = (o as Mesh).material;
    if (!m) return;
    const arr = Array.isArray(m) ? m : [m];
    const cloned = arr.map((x) => {
      if (!mats.has(x.name)) mats.set(x.name, x.clone() as never);
      return mats.get(x.name) as Material;
    });
    (o as Mesh).material = Array.isArray(m) ? cloned : cloned[0];
  });
  const lightMat = (n: string) => mats.get(n);
  const glow = (n: string, intensity: number) => { const m = lightMat(n); if (m && 'emissiveIntensity' in m) m.emissiveIntensity = intensity; };
  for (const n of ['light_front', 'light_rear', 'light_indicator_L', 'light_indicator_R', 'light_reverse']) glow(n, 0);

  const interiorDetail = byName.get('interior_detail');
  const interiorSimple = byName.get('interior_simple');
  if (interiorDetail) interiorDetail.visible = false;
  let steerDeg = 0;

  return {
    root,
    found,
    missing,
    isOpenable: (p) => hinges.has(p),
    setOpen(part, fraction) {
      const h = hinges.get(part);
      if (!h) return;
      h.value = Math.max(0, Math.min(1, fraction));
      apply(h, h.maxRad * h.value);
    },
    setSteering(rad) {
      knuckles.forEach((k, i) => {
        if (!k || i > 1) return;
        k.quaternion.copy(knuckleBase[i]!).multiply(new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), rad));
      });
      steerDeg = (rad * 180) / Math.PI * v.steering.rackRatio;
      if (swHinge) apply(swHinge, (steerDeg * Math.PI) / 180);
    },
    steeringWheelDeg: () => steerDeg,
    setWheelSpin(angles) {
      wheelHinges.forEach((h, i) => { if (h) apply(h, angles[i]); });
    },
    setPedals(p) {
      for (const pd of pedals) if (pd) apply(pd.h, pd.h.maxRad * Math.max(0, Math.min(1, p[pd.key] ?? 0)));
    },
    setGauges(g) {
      const val = { rpm: g.rpm, speed: g.speedKmh, temp: g.tempC, fuel: g.fuel };
      for (const n of needles) {
        if (!n) continue;
        const x = val[n.key];
        if (x === undefined) continue;
        const t = Math.max(0, Math.min(1, (x - n.min) / (n.max - n.min)));
        apply(n.h, ((n.a0 + (n.a1 - n.a0) * t) * Math.PI) / 180);
      }
    },
    setLights(l) {
      glow('light_front', l.high ? 6 : l.low ? 3.5 : l.drl ? 1.5 : 0);
      glow('light_rear', l.brake ? 5 : l.tail || l.low ? 1.6 : 0);
      glow('light_reverse', l.reverse ? 3 : 0);
      glow('light_indicator_L', l.indicatorL ? 4 : 0);
      glow('light_indicator_R', l.indicatorR ? 4 : 0);
    },
    setInteriorDetail(on) {
      if (interiorDetail) interiorDetail.visible = on;
      if (interiorSimple) interiorSimple.visible = !on;
    },
    setPaint(hex) {
      const m = mats.get('paint');
      if (m && m.color) m.color.set(hex);
    },
    dispose() {
      // eliberează geometria, materialele și texturile; întoarce câte resurse au fost eliberate
      let n = 0;
      const textures = new Set<Texture>();
      root.traverse((o) => {
        const mesh = o as Mesh;
        if (mesh.geometry) { mesh.geometry.dispose(); n++; }
      });
      for (const m of mats.values()) {
        for (const val of Object.values(m)) if (val && (val as Texture).isTexture) textures.add(val as Texture);
        m.dispose();
        n++;
      }
      for (const t of textures) { t.dispose(); n++; }
      root.removeFromParent();
      if (root instanceof Group) root.clear();
      return n;
    }
  };
}
